import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import IndexSearch from 'components/SearchModal/IndexSearch';

import { useUserInfo } from 'hooks/useUserInfo';

import { algolia } from 'settings';

/**
 * @typedef {object} SearchModalProps
 * @property {boolean} show Whether to show the modal or not.
 * @property {Function} onHide Function to call when the modal is closed.
 * @property {string} [query=''] The current search query. Defaults to an empty string. Default is `''`
 * @property {Function} [updateQuery=null] A function to call when the search query is updated. Gets passed the current
 *   query. Default is `null`
 */

/**
 * A controlled search-modal component using Algolia search toolling, displayed inside of a Modal component.
 *
 * @param {SearchModalProps} props Props
 * @returns {JSX.Element} SearchModal component with children.
 */

const SearchModal = ({ show, onHide, query = '', updateQuery = null }) => {
  const { user: rawUser, loaded: userLoaded } = useUserInfo();

  const user = useMemo(() => {
    if (!userLoaded) return {};
    return rawUser || {};
  }, [rawUser, userLoaded]);

  const { id: searchId, searchKey, hitsPerPage, attributesToSnippet } = algolia;

  return (
    <IndexSearch
      algoliaId={searchId}
      algoliaSearchKey={searchKey}
      hitsPerPage={hitsPerPage}
      attributesToSnippet={attributesToSnippet}
      onHide={onHide}
      defaultQuery={query}
      onQuery={updateQuery}
      salesCompanion
      pages
      exhibitionTiles
      user={user}
      isVisible={show}
    />
  );
};

SearchModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  query: PropTypes.string,
  updateQuery: PropTypes.func,
};

export default React.memo(SearchModal);
